import React, { FC, ReactNode, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { Card, Col, Row } from 'antd';
import { faChevronRight, Icon } from '@/ui/components';

export const COMPACT_STICKY_CARDS_QUERY = '(max-width: 991px)';

export type StickyCardPanel = {
  key: string;
  label: string;
  content: ReactNode;
};

export const useCompactStickyCards = () => {
  const [compact, setCompact] = useState(
    () => window.matchMedia(COMPACT_STICKY_CARDS_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(COMPACT_STICKY_CARDS_QUERY);
    const onChange = () => setCompact(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return compact;
};

const CompactRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  height: 100%;
  min-width: 0;
  flex: 1 1 auto;
`;

const PanelCard = styled(Card)<{ $expanded: boolean }>`
  flex-grow: ${({ $expanded }) => ($expanded ? 1 : 0)};
  flex-shrink: 0;
  flex-basis: 44px;
  height: 100%;
  min-width: 0;
  overflow: hidden;
  transition: flex-grow 0.35s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  .ant-card-body {
    height: 100%;
    overflow: ${({ $expanded }) => ($expanded ? 'auto' : 'hidden')};
    padding: ${({ $expanded }) => ($expanded ? undefined : '4px')};
    display: flex;
    align-items: center;
    justify-content: ${({ $expanded }) =>
      $expanded ? 'flex-start' : 'center'};
  }
`;

const PanelContent = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  animation: sticky-card-reveal 0.35s ease;

  @keyframes sticky-card-reveal {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ExpandButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: #595959;
  cursor: pointer;

  span {
    writing-mode: vertical-rl;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.1;
    max-height: 64px;
    overflow: hidden;
  }
`;

export const InspectionStickyCards: FC<{ panels: StickyCardPanel[] }> = ({
  panels,
}) => {
  const compact = useCompactStickyCards();
  const [activeKey, setActiveKey] = useState(panels[0]?.key);

  useEffect(() => {
    if (!panels.some((panel) => panel.key === activeKey)) {
      setActiveKey(panels[0]?.key);
    }
  }, [activeKey, panels]);

  if (!compact) {
    const span = 24 / panels.length;
    return (
      <Row
        gutter={16}
        className="h-100 flex-grow-1"
        data-testid="inspection-sticky-cards"
      >
        {panels.map((panel) => (
          <Col span={span} key={panel.key}>
            <Card size="small" className="h-100">
              {panel.content}
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <CompactRow data-testid="inspection-sticky-cards" data-compact="true">
      {panels.map((panel) => {
        const expanded = panel.key === activeKey;
        return (
          <PanelCard
            key={panel.key}
            $expanded={expanded}
            size="small"
            data-testid={`sticky-card-${panel.key}`}
            data-expanded={expanded ? 'true' : 'false'}
          >
            <PanelContent key={expanded ? 'open' : 'closed'}>
              {expanded ? (
                panel.content
              ) : (
                <ExpandButton
                  type="button"
                  aria-expanded={false}
                  aria-label={panel.label}
                  onClick={() => setActiveKey(panel.key)}
                >
                  <Icon icon={faChevronRight} />
                  <span>{panel.label}</span>
                </ExpandButton>
              )}
            </PanelContent>
          </PanelCard>
        );
      })}
    </CompactRow>
  );
};
